import React, { useState } from "react";
import { Subheading } from "../../../design/heading";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import { Textarea } from "../../../design/textarea";
import { Select } from "../../../design/select";
import { Field, Fieldset, FieldGroup, Label } from "../../../design/fieldset";
import { useSupervisor } from "../../../contexts/SupervisorContext";
import { Supervisor, SupervisorLanguage, PrimaryLanguage, LANGUAGE_LABELS } from "@visitproof/types";
import { Divider } from "../../../design/divider";
import { Badge } from "../../../design/badge";
import { PlusIcon, TrashIcon, PencilIcon } from "@heroicons/react/16/solid";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../../design/description-list";

interface Props {
  supervisor: Supervisor;
}

const SupervisorLanguagesCard: React.FC<Props> = ({ supervisor }) => {
  const { updateSupervisor } = useSupervisor();
  const [isOpen, setIsOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [language, setLanguage] = useState<PrimaryLanguage>("english");
  const [languageOther, setLanguageOther] = useState("");
  const [notes, setNotes] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);

  const languages = supervisor.languages || [];

  const resetForm = () => {
    setLanguage("english");
    setLanguageOther("");
    setNotes("");
    setIsPrimary(false);
    setEditingIndex(null);
  };

  const openAddDialog = () => {
    resetForm();
    setIsOpen(true);
  };

  const openEditDialog = (index: number) => {
    const lang = languages[index];
    setLanguage(lang.language);
    setLanguageOther(lang.languageOther || "");
    setNotes(lang.notes || "");
    setIsPrimary(lang.isPrimary || false);
    setEditingIndex(index);
    setIsOpen(true);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Build language object without undefined values (Firestore doesn't accept undefined)
      const newLanguage: SupervisorLanguage = {
        language,
        isPrimary,
      };
      if (language === "other" && languageOther) {
        newLanguage.languageOther = languageOther;
      }
      if (notes) {
        newLanguage.notes = notes;
      }

      let updatedLanguages: SupervisorLanguage[];
      if (editingIndex !== null) {
        // Update existing
        updatedLanguages = [...languages];
        updatedLanguages[editingIndex] = newLanguage;
      } else {
        // Add new
        updatedLanguages = [...languages, newLanguage];
      }

      // If this language is marked as primary, unmark others
      if (isPrimary) {
        updatedLanguages = updatedLanguages.map((lang, idx) => ({
          language: lang.language,
          isPrimary: idx === (editingIndex ?? updatedLanguages.length - 1),
          ...(lang.languageOther ? { languageOther: lang.languageOther } : {}),
          ...(lang.notes ? { notes: lang.notes } : {}),
        }));
      }

      // Calculate isBilingual - true if there are languages other than just English
      const isBilingual = updatedLanguages.some(
        (lang) => lang.language !== "english"
      );

      await updateSupervisor(supervisor.id, {
        languages: updatedLanguages,
        isBilingual,
      });

      setIsOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving language:", error);
    }
    setIsSubmitting(false);
  };

  const handleDelete = async (index: number) => {
    if (!window.confirm("Are you sure you want to remove this language?")) return;

    setIsSubmitting(true);
    try {
      const updatedLanguages = languages.filter((_, i) => i !== index);
      const isBilingual = updatedLanguages.some(
        (lang) => lang.language !== "english"
      );

      await updateSupervisor(supervisor.id, {
        languages: updatedLanguages,
        isBilingual,
      });
    } catch (error) {
      console.error("Error deleting language:", error);
    }
    setIsSubmitting(false);
  };

  const getLanguageLabel = (lang: SupervisorLanguage): string => {
    if (lang.language === "other" && lang.languageOther) {
      return lang.languageOther;
    }
    return LANGUAGE_LABELS[lang.language] || lang.language;
  };

  return (
    <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <div className="flex items-center justify-between">
        <Subheading>Languages</Subheading>
        <Button outline onClick={openAddDialog}>
          <PlusIcon className="w-4 h-4 mr-1" />
          Add Language
        </Button>
      </div>
      <Divider className="my-4" />

      {languages.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            No languages specified. Default is English.
          </p>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            Add languages to help match this monitor with families.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {languages.map((lang, index) => (
            <div
              key={index}
              className="p-4 bg-zinc-50 dark:bg-zinc-800 rounded-lg"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-zinc-900 dark:text-zinc-100">
                    {getLanguageLabel(lang)}
                  </span>
                  {lang.isPrimary && (
                    <Badge color="blue">Primary</Badge>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    plain
                    onClick={() => openEditDialog(index)}
                    disabled={isSubmitting}
                  >
                    <PencilIcon className="w-4 h-4" />
                  </Button>
                  <Button
                    plain
                    onClick={() => handleDelete(index)}
                    disabled={isSubmitting}
                  >
                    <TrashIcon className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
              {lang.notes && (
                <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
                  {lang.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Bilingual Status */}
      {languages.length > 0 && (
        <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700">
          <DescriptionList>
            <DescriptionTerm>Bilingual Status</DescriptionTerm>
            <DescriptionDetails>
              <Badge color={supervisor.isBilingual ? "green" : "zinc"}>
                {supervisor.isBilingual ? "Bilingual" : "English Only"}
              </Badge>
            </DescriptionDetails>
          </DescriptionList>
        </div>
      )}

      {/* Add/Edit Language Dialog */}
      <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
        <DialogTitle>
          {editingIndex !== null ? "Edit Language" : "Add Language"}
        </DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Language</Label>
                <Select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as PrimaryLanguage)}
                >
                  {Object.entries(LANGUAGE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>

              {language === "other" && (
                <Field>
                  <Label>Specify Language</Label>
                  <Input
                    type="text"
                    value={languageOther}
                    placeholder="Enter language name"
                    onChange={(e) => setLanguageOther(e.target.value)}
                  />
                </Field>
              )}

              <Field>
                <Label>Notes (dialect, proficiency, certifications, etc.)</Label>
                <Textarea
                  value={notes}
                  placeholder="e.g., Native speaker, Mexican Spanish dialect, ASL certified interpreter"
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </Field>

              <Field>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isPrimary}
                    onChange={(e) => setIsPrimary(e.target.checked)}
                    className="w-4 h-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-zinc-700 dark:text-zinc-300">
                    This is the primary/native language
                  </span>
                </label>
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={isSubmitting || (language === "other" && !languageOther.trim())}
            onClick={handleSubmit}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SupervisorLanguagesCard;
